import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';

import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { type DynamicAction } from '@shared/registry';

import { SvgIcon } from '../svg-icon/svg-icon';

import { type ButtonConfig } from './button.model';

@Component({
  selector: 'app-button',
  templateUrl: './button.html',
  styleUrl: './button.scss',
  imports: [ProgressSpinnerModule, SvgIcon, NgClass, ButtonModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Button {
  readonly config = input.required<ButtonConfig>();
  readonly disabled = input<boolean>(false);

  readonly action = output<DynamicAction | undefined>();

  protected readonly iconSize = computed((): string => {
    const remToPx = (rem: number): string => `${rem * 16}px`;

    let sizeInRem: number = 1;
    const config = this.config();

    if (config.iconOnly) {
      switch (config.size) {
        case 'small':
          sizeInRem = 0.875;
          break;
        case 'medium':
          sizeInRem = 1;
          break;
        case 'large':
          sizeInRem =
            parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--p-button-icon-only-size')) || 1.5;
          break;
        default:
          sizeInRem = 1;
          break;
      }
    } else {
      switch (config.size) {
        case 'small':
          sizeInRem =
            parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--p-button-small-font-size')) || 1;
          break;
        case 'medium':
          sizeInRem = 1;
          break;
        case 'large':
          sizeInRem =
            parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--p-button-large-font-size')) ||
            1.125;
          break;
        default:
          sizeInRem = 1;
          break;
      }
    }

    return remToPx(sizeInRem);
  });

  protected readonly iconColor = computed((): string => {
    // Use currentColor to inherit the text color, including hover states
    return 'currentColor';
  });

  protected readonly isLoading = computed((): boolean => {
    return !!this.config().loading;
  });

  protected readonly primeNgSize = computed((): 'small' | 'large' | null => {
    const config = this.config();
    if (!config.size || config.size === 'medium') {
      return null;
    }
    return config.size;
  });

  protected onClick(): void {
    if (!this.disabled() && !this.config().loading) {
      this.action.emit(this.config().action);
    }
  }
}
